import * as Popover from "@radix-ui/react-popover";

import { useEffect, useMemo, useState } from "react";

import { Button } from "react-migration/components/Button";
import { MoveToStageForm } from "react-migration/domains/sites/components/MoveSavedSites/MoveToStageForm";
import { SelectWorkspace } from "react-migration/domains/sites/components/MoveSavedSites/SelectBox";
import { getEditableDrawing } from "src/js/stores/drawing/actions/getEditableDrawing";
import { getSelectedGeometry } from "src/js/stores/drawing/actions/getSelectedGeometry";
import { removeDrawingFromLocalState } from "src/js/stores/drawing/actions/removeDrawingFromLocalState";
import { setSelectedGeometry } from "src/js/stores/drawing/actions/setSelectedGeometry";
import { siteStore } from "src/js/stores/site/store";
import { updateDrawing } from "src/js/stores/drawing/actions/updateDrawing";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useWorkspacesList } from "react-migration/domains/workspaces/hooks/useWorkspacesList";

export function MoveToDropdown() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { selectedWorkspaceId } = useSnapshot(siteStore);
  const { list: allWorkspaces, loading } = useWorkspacesList();

  const selectedDrawing = getSelectedGeometry();
  const editableDrawing = getEditableDrawing();
  const drawing = selectedDrawing || editableDrawing;

  const [selectedWorkspace, setSelectedWorkspace] = useState<SelectWorkspace | undefined>(undefined);

  const previousWorkspace = useMemo(() => {
    const workspaceId = drawing?.workspaceId || selectedWorkspaceId;
    if (!workspaceId) return undefined;
    return allWorkspaces.find((candidate) => candidate.id === workspaceId);
  }, [allWorkspaces, selectedWorkspaceId, drawing?.workspaceId]);

  useEffect(() => {
    if (!previousWorkspace) return;
    setSelectedWorkspace(previousWorkspace);
  }, [previousWorkspace]);

  const handleCancel = () => {
    setIsOpen(false);
    setSelectedWorkspace(previousWorkspace);
  };

  const currentWorkspaceId = drawing?.workspaceId || selectedWorkspaceId;
  const isMovingToAnotherWorkspace = selectedWorkspace && selectedWorkspace.id !== currentWorkspaceId;

  const handleMove = () => {
    if (selectedWorkspace && drawing) {
      updateDrawing(drawing, true, { workspaceId: selectedWorkspace.id });
      if (isMovingToAnotherWorkspace) {
        removeDrawingFromLocalState(drawing.id);
        setSelectedGeometry("");
      }
    }
    setIsOpen(false);
  };

  const moveDisabled = !selectedWorkspace || loading;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <Popover.Trigger asChild>
        <Button
          variant="tertiary"
          size="md"
          leadIcon="lt-share-forward-line"
          tailIcon="lt-arrow-down-s-line"
          data-testid="move-to-dropdown-trigger"
        >
          {t("drawings.move_to")}
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={5}
          className="bg-background-surface rounded-lg border border-border-action shadow-lg"
          data-testid="move-to-dropdown-content"
        >
          <MoveToStageForm
            title={t("drawings.move_to")}
            cancelLabel={t("general.cancel")}
            submitLabel={t("general.move")}
            workspaceSelect={{
              label: t("drawings.select_a_workspace"),
              workspaces: allWorkspaces,
              selectedWorkspace,
              onChange: setSelectedWorkspace,
              loading,
            }}
            disabled={moveDisabled}
            onSubmit={handleMove}
            onCancel={handleCancel}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
